import { fetchAuth } from "../common/api";
import { encodeId } from "../common/hashid.js";

export class RpeSessionApiError extends Error {
  constructor(code, message) {
    super(message || code || "rpe_session_error");
    this.name = "RpeSessionApiError";
    this.code = code || "rpe_session_error";
  }
}

async function readBody(res) {
  return res.json().catch(() => ({}));
}

async function throwIfNotOk(res, fallbackCode) {
  const body = await readBody(res);
  if (res.ok) return body;
  throw new RpeSessionApiError(body.error || fallbackCode, body.message || body.error);
}

function sessionPath(sessionId, suffix = "") {
  return `/rpe_sessions/${encodeId(sessionId)}${suffix}`;
}

export async function closeRpeSession(sessionId, clubId, { targetDate = null } = {}) {
  const res = await fetchAuth(sessionPath(sessionId, "/close"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...(clubId ? { club_id: clubId } : {}),
      ...(targetDate ? { target_date: targetDate } : {}),
    }),
  });
  if (res.status === 409) return { message: "already closed" };
  return throwIfNotOk(res, "error_close_session");
}

export async function deleteRpeSession(sessionId) {
  const res = await fetchAuth(sessionPath(sessionId), { method: "DELETE" });
  return throwIfNotOk(res, "error_delete_session");
}

export async function applyCoachRpeToSession(sessionId, { close = false, targetDate = null } = {}) {
  const res = await fetchAuth(sessionPath(sessionId, "/apply_coach_rpe"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      close,
      target_date: targetDate || undefined,
    }),
  });
  return throwIfNotOk(res, "error_quick_send");
}

export async function saveManualRpeEntries(sessionId, entries, targetDate = null) {
  const res = await fetchAuth("/rpe_feedback/manual", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      session_id: sessionId,
      target_date: targetDate || undefined,
      entries,
    }),
  });
  return throwIfNotOk(res, "error_save_manual_entries");
}

export async function patchRpeSession(sessionId, payload) {
  const res = await fetchAuth(sessionPath(sessionId), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return throwIfNotOk(res, "error_update_session");
}

export function isRpeSessionOpen(session) {
  if (!session) return false;
  return Boolean(session.is_open || !session.closed_at);
}

export async function fetchRpeAutoCloseSettings() {
  const res = await fetchAuth("/rpe_auto_close_settings");
  return throwIfNotOk(res, "error_load_auto_close");
}

export async function patchRpeAutoCloseSettings(payload) {
  const res = await fetchAuth("/rpe_auto_close_settings", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return throwIfNotOk(res, "error_save_auto_close");
}

export function getRpeSessionMissingCount(session) {
  if (!session) return 0;
  if (Array.isArray(session.missing_players)) return session.missing_players.length;
  const targets = Array.isArray(session.target_player_ids) ? session.target_player_ids.length : 0;
  const done = Number(session.responses_count ?? session.responders?.length ?? 0);
  if (!targets) return 0;
  return Math.max(0, targets - (Number.isFinite(done) ? done : 0));
}
